import {
  Box,
  Container,
  Heading,
  Text,
  Grid,
  GridItem,
  VStack,
  Avatar,
  Flex,
  Icon,
  SimpleGrid,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Brain, Code, LucideIcon, Rocket } from 'lucide-react';

const MotionBox = motion(Box);

const ExperienceCard = ({
  icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) => (
  <Box
    p={{ base: 4, md: 6 }}
    borderRadius='xl'
    bg='rgba(11, 61, 145, 0.3)'
    border='1px solid'
    borderColor='brand.accent'
    transition='all 0.3s'
    _hover={{
      transform: 'translateY(-4px)',
      boxShadow: '0 4px 20px rgba(70, 255, 255, 0.2)',
    }}
    role='article'>
    <Icon as={icon} color='brand.accent' boxSize={{ base: 6, md: 8 }} mb={4} aria-hidden='true' />
    <Text color='brand.accent' fontWeight='bold' mb={2} fontSize={{ base: 'md', md: 'lg' }}>
      {title}
    </Text>
    <Text color='gray.300' fontSize={{ base: 'sm', md: 'md' }}>
      {description}
    </Text>
  </Box>
);

const TeamMember = ({
  name,
  role,
  quote,
  imageSrc,
}: {
  name: string;
  role: string;
  quote: string;
  imageSrc: string;
}) => (
  <Box
    p={{ base: 4, md: 6 }}
    borderRadius='xl'
    bg='rgba(11, 61, 145, 0.3)'
    border='1px solid'
    borderColor='brand.accent'
    role='article'>
    <VStack spacing={4} align='stretch'>
      <Flex align='center' direction={{ base: 'column', sm: 'row' }}>
        <Avatar
          size={{ base: 'lg', md: 'xl' }}
          name={name}
          src={imageSrc}
          mb={{ base: 4, sm: 0 }}
          mr={{ base: 0, sm: 6 }}
        />
        <Box textAlign={{ base: 'center', sm: 'left' }}>
          <Heading as='h3' size={{ base: 'md', md: 'lg' }} color='brand.light' mb={2}>
            {name}
          </Heading>
          <Text color='brand.accent' fontSize={{ base: 'sm', md: 'md' }}>
            {role}
          </Text>
        </Box>
      </Flex>
      <Text color='gray.300' fontStyle='italic' fontSize={{ base: 'sm', md: 'md' }}>
        "{quote}"
      </Text>
    </VStack>
  </Box>
);

const About = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <Box
      id='about'
      py={{ base: 12, md: 20 }}
      bg='brand.dark'
      bgImage="linear-gradient(to bottom, rgba(2, 8, 20, 0.95), rgba(2, 8, 20, 0.95)), url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=2000&q=80')"
      bgAttachment={{ base: 'scroll', md: 'fixed' }}
      bgSize='cover'
      bgPosition='center'>
      <Container maxW='1200px' px={{ base: 4, md: 6 }}>
        <VStack spacing={{ base: 10, md: 16 }}>
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={{ base: 8, md: 12 }}>
            <GridItem>
              <MotionBox
                ref={ref}
                initial={{ opacity: 0, x: -50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}>
                <VStack align='stretch' spacing={{ base: 6, md: 8 }}>
                  <Heading
                    as='h2'
                    size={{ base: 'lg', md: 'xl' }}
                    color='brand.light'
                    letterSpacing='wider'>
                    Our Story
                  </Heading>

                  <Box
                    p={{ base: 4, md: 6 }}
                    borderRadius='xl'
                    bg='rgba(11, 61, 145, 0.3)'
                    border='1px solid'
                    borderColor='brand.accent'>
                    <Text color='gray.300' fontSize={{ base: 'md', md: 'lg' }} mb={4}>
                      GallifreyAI was born from a simple observation: the tech industry often
                      expects everyone to work the same way. As a neurodivergent founder, I saw an
                      opportunity to create tools that embrace different ways of thinking and
                      working.
                    </Text>
                    <Text color='gray.300' fontSize={{ base: 'md', md: 'lg' }}>
                      Our mission isn't just about building better developer tools—it's about
                      creating a more inclusive tech industry where neurodivergent minds can thrive
                      and innovate without barriers.
                    </Text>
                  </Box>
                </VStack>
              </MotionBox>
            </GridItem>

            <GridItem>
              <MotionBox
                initial={{ opacity: 0, x: 50 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.8 }}>
                <VStack spacing={{ base: 4, md: 6 }}>
                  <ExperienceCard
                    icon={Brain}
                    title='Neurodiversity as Strength'
                    description='We leverage different perspectives and thinking patterns to create innovative solutions that work for everyone.'
                  />
                  <ExperienceCard
                    icon={Code}
                    title='Adaptive Development'
                    description='Building tools that adapt to different working styles, making development more accessible and enjoyable for all.'
                  />
                  <ExperienceCard
                    icon={Rocket}
                    title='Future of Work'
                    description='Creating a future where neurodivergent developers can work in ways that feel natural and productive to them.'
                  />
                </VStack>
              </MotionBox>
            </GridItem>
          </Grid>

          <VStack spacing={{ base: 6, md: 8 }} width='100%'>
            <Heading
              as='h2'
              size={{ base: 'lg', md: 'xl' }}
              color='brand.light'
              letterSpacing='wider'
              textAlign='center'>
              Meet Our Team
            </Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }} width='100%'>
              <TeamMember
                name='Cristian Sarmiento'
                role='Founder & CEO'
                quote="Being neurodivergent has given me a unique perspective on how we can improve developer workflows. At GallifreyAI, we're not just building tools—we're creating a more inclusive future for software development."
                imageSrc='https://media.licdn.com/dms/image/v2/D4D03AQGf-DgQ-l7SLQ/profile-displayphoto-shrink_800_800/B4DZRzl3dPGUAc-/0/1737106094585?e=1743033600&v=beta&t=DsgMZoeceGbeh4-QW0pikXmwFEjOqKK0C13KTUTky3s'
              />
              <TeamMember
                name='Jose Maria Peirano'
                role='Software Engineer Manager'
                quote="Our diverse perspectives drive our innovation. We're creating AI solutions that understand and adapt to different ways of thinking and working."
                imageSrc='https://media.licdn.com/dms/image/v2/D4D03AQG0m0hFf7Bv-A/profile-displayphoto-shrink_400_400/profile-displayphoto-shrink_400_400/0/1708541436740?e=1743033600&v=beta&t=D9BTFaQx235K15XgeTBSYhicJz2XTR4YjPYeUFEk93U'
              />
            </SimpleGrid>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default About;
